import { modal, ModalParams } from '@codeleap/modals'
import { View, Text, Button } from '@/components'
import { useForm } from '@codeleap/common'
import { AppForms, variantProvider } from '@/app'
import { APIClient } from '@/services'
import { CommunityReportReason } from '@/types'
import { ReportOverlay } from './ReportOverlay'
import React, { useCallback } from 'react'
import { ReportableContent } from '@/services/api/report'
import { ReportSuccessModal } from '../molecules/ReportSuccessModal'
import { SimpleList } from '@/components/SimpleList'

type ReportProps = {
  content: ReportableContent
}

const Report = (props: Partial<ModalParams> & ReportProps) => {
  const { toggle, content } = props

  const form = useForm(AppForms.reportFeedItem, {})
  const communityReportOptions = APIClient.Report.useCommunityReportOptions()
  const optionsReasons = communityReportOptions?.data?.report_reason.map(item => ({
    id: item.value,
    ...item,
  })) || []
  const report = APIClient.Report.useReport()

  const selectedReason = form.values.reason

  const handleSelectReason = (reason: CommunityReportReason['value']) => {
    if (selectedReason === reason) {
      form.setFieldValue('reason', null)
      return
    }

    form.setFieldValue('reason', reason)
  }

  const onSubmit = async () => {
    await report.mutateAsync({
      reason: selectedReason,
      ...content,
    })

    toggle()

    if ('feed_item' in content) {
      ReportOverlay.report(content.feed_item)
      return
    } else if ('comment' in content) {

      const comment = APIClient.Community.commentsManager.queryKeys.getRetrieveData(content?.comment)

      APIClient.Community.commentsManager.queryKeys.invalidateList({
        itemId: comment?.item
      })

      APIClient.Community.commentRepliesManager.queryKeys.invalidateList({
        commentId: comment?.id
      })
      
      APIClient.Community.refreshFeed()
    }
    
    ReportSuccessModal.open()
  }

  const renderButton = useCallback(({ item }: { item: CommunityReportReason }) => {
    const isSelected = selectedReason === item.value

    const wrapperStyles = {
      ...styles.wrapper,
      ...(isSelected ? styles['wrapper:selected'] : {})
    }

    const textStyles = {
      ...styles.text,
      ...(isSelected ? styles['text:selected'] : {})
    }

    return (
      <Button
        text={item.label}
        onPress={() => handleSelectReason(item.value)}
        debugName={`${item.value}_reportReason:pressed`}
        styles={{
          wrapper: wrapperStyles,
          text: textStyles,
          icon: styles.icon,
        }}
        rightIcon={selectedReason === item.value ? 'check' : undefined}
      />
    )
  }, [selectedReason])

  return (
    <View variants={['gap:2', 'column']}>
      <Text variants={['h4', 'color:neutral9']}>Select a reason</Text>

      <Text variants={['p3', 'color:neutral9']}>You won't see posts from this user in your feed anymore.</Text>

      <SimpleList<CommunityReportReason & { id: string }>
        data={optionsReasons}
        renderItem={renderButton}
        keyExtractor={(item) => item?.id}
        style={styles.listWrapper}
        loading={communityReportOptions?.isLoading || communityReportOptions?.isFetching}
      />

      <View variants={['row', 'gap:1', 'fullWidth']}>
        <Button text='Cancel' variants={['flex', 'outline']} onPress={toggle} debugName={'cancel:report'} />
        <Button text='Submit' variants={['flex']} disabled={!selectedReason} onPress={onSubmit} debugName={'submit:report'} loading={report.isPending} />
      </View>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(theme => ({
  wrapper: {
    ...theme.border.neutral2(theme.values.borderWidth.medium),
    backgroundColor: theme.colors.neutral2,
    borderRadius: theme.borderRadius.small,
    ...theme.presets.justifyStart,
    zIndex: 1000,
    transition: 'all 0.1s',
    '&:hover': {
      backgroundColor: theme.colors.neutral3,
    },
  },
  text: {
    color: theme.colors.neutral6,
  },
  'text:selected': {
    color: theme.colors.neutral6,
    fontWeight: '700',
    marginRight: 'auto',
  },
  'wrapper:selected': {
    ...theme.border.primary3(theme.values.borderWidth.medium),
    backgroundColor: theme.colors.primary1,
    '&:hover': {
      backgroundColor: theme.colors.neutral3,
    },
  },
  icon: {
    color: theme.colors.neutral6,
  },
  listWrapper: {
    overflowX: 'hidden',
    overflowY: 'auto',
    borderRadius: theme.borderRadius.small,
    ...theme.spacing.gap(1),
    ...theme.presets.column,
    ...theme.presets.fullWidth,
  },
}), true)

export const ReportModal = modal<ReportProps>()
  .content(props => <Report {...props} />)
  .props({
    variants: ['centered'],
    showClose: false,
    styles: {
      box: { maxWidth: 343, width: '100vw' },
      wrapper: { zIndex: 9999 },
    }
  })
